/**
 * 欢迎礼包 / 体验金签名领取
 * challenge + personal_sign（EOA 或 Privy 嵌入式钱包）
 */
import request from '@/utils/request';
import {
  getPrivySessionUser,
  isPrivyCustodialLogin,
  signMessageWithPrivyWallet,
} from '@/utils/privy';
import { getWalletSigningProvider } from '@/utils/wallet-provider';
import { generateBotProof } from '@/utils/botDetector';

function assertChallengeAddress(signedAddress, expectedAddress) {
  const got = String(signedAddress || '')
    .trim()
    .toLowerCase();
  const want = String(expectedAddress || '')
    .trim()
    .toLowerCase();
  if (want && got && got !== want) {
    throw new Error('signed address mismatch with challenge wallet');
  }
}

/**
 * @param {{ purpose?: 'POINTS'|'TRIAL', amount?: number|string }} [params]
 */
export function getWelcomePackChallenge(params = {}) {
  const query = {};
  if (params.purpose) query.purpose = params.purpose;
  if (params.amount != null && params.amount !== '') {
    query.amount = String(params.amount);
  }
  return request.get('/liyuan-trade/wallet/welcome-pack/challenge', query);
}

export function getWelcomePackStatus() {
  return request.get('/liyuan-trade/wallet/welcome-pack/status');
}

/**
 * @param {{ address: string, message: string, signature: string, botProof?: string }} body
 */
export function claimWelcomePack(body) {
  return request.post('/liyuan-trade/wallet/welcome-pack/claim', {
    address: body?.address,
    message: body?.message,
    signature: body?.signature,
    botProof: body?.botProof,
  });
}

/**
 * 对 challenge.message 做 personal_sign（Privy 托管优先，否则当前 EOA provider）
 * @param {string} message
 * @param {string} [expectedAddress]
 * @returns {Promise<{ address: string, signature: string, message: string }>}
 */
export async function signRewardClaimMessage(message, expectedAddress = '') {
  const text = String(message || '').trim();
  if (!text) throw new Error('empty reward claim message');

  if (isPrivyCustodialLogin()) {
    const privyUser = await getPrivySessionUser();
    if (!privyUser) {
      throw new Error('Privy session missing for reward claim');
    }
    const signed = await signMessageWithPrivyWallet(privyUser, text);
    const address = String(signed.walletAddress || '').trim();
    assertChallengeAddress(address, expectedAddress);
    return {
      address,
      signature: String(signed.signature || '').trim(),
      message: text,
    };
  }

  const provider = await getWalletSigningProvider();
  if (!provider?.request) {
    throw new Error('wallet provider unavailable for reward claim signature');
  }
  const accounts = await provider.request({ method: 'eth_accounts' });
  let address = String(expectedAddress || accounts?.[0] || '').trim();
  if (!address) {
    const req = await provider.request({ method: 'eth_requestAccounts' });
    address = String(req?.[0] || '').trim();
  }
  if (!address) throw new Error('no wallet address for reward claim');
  assertChallengeAddress(address, expectedAddress);
  const signature = await provider.request({
    method: 'personal_sign',
    params: [text, address],
  });
  return {
    address,
    signature: String(signature || '').trim(),
    message: text,
  };
}

let welcomePackClaimInFlight = null;

/**
 * 若有待领种子积分：拉 challenge → personal_sign → claim。
 * 无 pending / 无交易地址时静默跳过（不弹错）。
 * @returns {Promise<object|null>}
 */
export async function claimPendingWelcomePackIfAny() {
  if (welcomePackClaimInFlight) return welcomePackClaimInFlight;
  welcomePackClaimInFlight = (async () => {
    try {
      const status = await getWelcomePackStatus();
      if (!status?.hasPending) return null;
      const challenge = await getWelcomePackChallenge({ purpose: 'POINTS' });
      if (!challenge?.message || !challenge?.address) return null;
      const signed = await signRewardClaimMessage(challenge.message, challenge.address);
      let botProof = '';
      try {
        botProof = await generateBotProof('welcome_pack_claim');
      } catch (err) {
        console.warn('[welcome-pack] generateBotProof error', err);
      }
      return await claimWelcomePack({
        ...signed,
        botProof,
      });
    } catch (err) {
      const msg = String(err?.message || err || '');
      // 无钱包 / 用户拒签 / 暂无待领：不打断主流程
      if (
        /no_trade_address|先绑定|empty reward|provider unavailable|User rejected|denied|nothing_pending|暂无待领/i.test(
          msg
        )
      ) {
        return null;
      }
      console.warn('[welcome-pack] claimPendingWelcomePackIfAny failed', err);
      return null;
    } finally {
      welcomePackClaimInFlight = null;
    }
  })();
  return welcomePackClaimInFlight;
}
